// Ensures an array contains every possible value of a string union.
const exhaustiveStringTuple = () => (...x) => x;
const ALL_RANKS = exhaustiveStringTuple()('2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A');
const ALL_SUITS = exhaustiveStringTuple()('c', 'd', 'h', 's');
const ALL_CARDS = ALL_RANKS.reduce((acc, rank) => {
  acc.push(...ALL_SUITS.map(suit => `${rank}${suit}`));
  return acc;
}, []);

/**
 * Error class that denotes a provided value does not represent a valid {@link Card}.
 */
class InvalidCardError extends Error {
  constructor(cardString) {
    super(`Invalid card: "${cardString}"`);
  }
}

/**
 * Determines if a string represents a valid {@link Rank} type.
 * @param {string} str Any string value.
 * @returns {boolean} True if `str` represents a valid {@link Rank} type, false otherwise.
 */
const isRank = str => str.length === 1 && ALL_RANKS.some(rank => rank === str);

/**
 * Determines if a string represents a valid {@link Suit} type.
 * @param {string} str Any string value.
 * @returns {boolean} True if `str` represents a valid {@link Suit} type, false otherwise.
 */
const isSuit = str => str.length === 1 && ALL_SUITS.some(suit => suit === str);

/**
 * Determines if a string represents a valid {@link Card} type.
 * @param {string} str Any string value.
 * @returns {boolean} True if `str` represents a valid {@link Card} type, false otherwise.
 */
const isCard = str => str.length === 2 && isRank(str[0]) && isSuit(str[1]);

/**
 * Ensures that string represents a valid {@link Card} type, otherwise throws.
 * @param {Card} card A string that should represent a {@link Card} type.
 * @throws {InvalidCardError} if the provided string is not a valid {@link Card} type.
 */
const assertCard = card => {
  if (!isCard(card)) {
    throw new InvalidCardError(card);
  }
};

/**
 * Returns the {@link Rank} of the provided string if it represents a valid {@link Card} type.
 * @param {Card} card A string that should represent a {@link Card} type.
 * @returns {boolean} The {@link Rank} of the card.
 * @throws {InvalidCardError} if the provided string is not a valid {@link Card} type.
 */
const getRank = card => {
  if (!isCard(card)) {
    throw new InvalidCardError(card);
  }
  return card[0];
};

/**
 * Returns the {@link Suit} of the provided string if it represents a valid {@link Card} type.
 * @param {Card} card A string that should represent a {@link Card} type.
 * @returns {Suit} The {@link Suit} of the card.
 * @throws {InvalidCardError} if the provided string is not a valid {@link Card} type.
 */
const getSuit = card => {
  if (!isCard(card)) {
    throw new InvalidCardError(card);
  }
  return card[1];
};

/**
 * All possible card ranks in a standard game of poker.
 */

/**
 * All possible card suits in a standard game of poker.
 */

/**
 * All possible cards in a standard game of poker.
 */

/**
 * An array of cards representing a player's hand.
 */

/**
 * All possible high hand strengths in a standard game of poker.
 */
let HandStrength = /*#__PURE__*/function (HandStrength) {
  HandStrength[HandStrength["HighCard"] = 0] = "HighCard";
  HandStrength[HandStrength["OnePair"] = 1] = "OnePair";
  HandStrength[HandStrength["TwoPair"] = 2] = "TwoPair";
  HandStrength[HandStrength["ThreeOfAKind"] = 3] = "ThreeOfAKind";
  HandStrength[HandStrength["Straight"] = 4] = "Straight";
  HandStrength[HandStrength["Flush"] = 5] = "Flush";
  HandStrength[HandStrength["FullHouse"] = 6] = "FullHouse";
  HandStrength[HandStrength["FourOfAKind"] = 7] = "FourOfAKind";
  HandStrength[HandStrength["StraightFlush"] = 8] = "StraightFlush";
  HandStrength[HandStrength["RoyalFlush"] = 9] = "RoyalFlush";
  return HandStrength;
}({});

export { ALL_CARDS, ALL_RANKS, ALL_SUITS, HandStrength, InvalidCardError, assertCard, getRank, getSuit, isCard, isRank, isSuit };
//# sourceMappingURL=index.js.map
